import { Sparkles } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { msg, t } from '@/lib/i18n';
import { ErrorState, LoadingState } from '@/ui';
import s from './Assistant.module.css';
import type { AssistantContextInput } from './context';
import { AssistantMessage, LiveMessage, UserBubble } from './MessageView';
import type { useConversation } from './useConversation';

const DEFAULT_PROMPTS = [msg('What needs my attention today?'), msg('Which vehicles have no working device?'), msg('Summarise this page for me')];

export function Conversation({
  convo,
  context,
  onPrompt,
}: {
  convo: ReturnType<typeof useConversation>;
  context: AssistantContextInput | null;
  onPrompt: (text: string) => void;
}) {
  const { thread, live } = convo;
  const end = useRef<HTMLDivElement>(null);
  const persisted = thread.data?.messages ?? [];
  // Hide the stored copy of the turn that is still shown live.
  const liveIdx = live?.messageId ? persisted.findIndex((m) => m.id === live.messageId) : -1;
  const shown = liveIdx >= 0 ? persisted.slice(0, Math.max(0, liveIdx - 1)) : live ? persisted.filter((m) => m.status !== 'working') : persisted;

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [shown.length, live?.text, live?.tools.length, live?.proposals.length]);

  if (thread.isLoading && !live) return <LoadingState />;
  if (thread.error && !live) return <ErrorState error={thread.error} onRetry={() => void thread.refetch()} compact />;

  if (!shown.length && !live) {
    const prompts = context?.suggestions?.length ? context.suggestions : DEFAULT_PROMPTS.map((p) => t(p));
    return (
      <div className={s.empty}>
        <span className={s.emptyIcon}>
          <Sparkles aria-hidden />
        </span>
        <p className={s.emptyTitle}>{t('Ask about anything on this page')}</p>
        <p className={s.emptyText}>{t('Answers come from your fleet data. Changes are proposed first; you decide what is applied.')}</p>
        <div className={s.prompts}>
          {prompts.map((p) => (
            <button key={p} type="button" className={s.prompt} onClick={() => onPrompt(p)}>
              {p}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={s.messages}>
      {shown.map((m) =>
        m.role === 'user' ? <UserBubble key={m.id} text={m.text} /> : <AssistantMessage key={m.id} text={m.text} parts={m.parts ?? []} status={m.status} />,
      )}
      {live && (
        <>
          <UserBubble text={live.userText} />
          <LiveMessage turn={live} />
        </>
      )}
      <div ref={end} />
    </div>
  );
}
